/**
 * OPS-02: one id per API call, shared with the API's logs, traces and error tracker. The browser makes the id
 * (so it is known even when the response never arrives) and sends it as X-Correlation-Id; the API echoes it.
 */
export const CORRELATION_HEADER = 'X-Correlation-Id';

/** How long after a failed call an error toast is assumed to be about that call. */
const FAILURE_WINDOW_MS = 5000;

let lastFailure: { id: string; at: number } | null = null;
/** SUP-01: kept after the toast has taken `lastFailure`, so a problem report can still name the call. */
let lastFailureEver: string | null = null;

/** "fe-" + 20 random hex characters: accepted by the API (8–64 of A-Z a-z 0-9 . _ -). */
export function newCorrelationId(): string {
  const bytes = new Uint8Array(10);
  crypto.getRandomValues(bytes);
  return 'fe-' + Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
}

/** Remembers a call that failed on the server (5xx) or never reached it, for the next error toast. */
export function recordFailedCall(id: string, now = Date.now()): void {
  lastFailure = { id, at: now };
  lastFailureEver = id;
}

/** The id of the last failed call if it failed just now, without consuming it. */
export function lastFailedCallId(now = Date.now()): string | null {
  return lastFailure && now - lastFailure.at <= FAILURE_WINDOW_MS ? lastFailure.id : null;
}

/** SUP-01: the id of the last call that failed, however long ago (a problem report names it, the reporter may have taken a while). */
export function lastFailedCallEver(): string | null {
  return lastFailureEver;
}

/** The id of a call that just failed, once: the first error toast after the failure shows it. */
export function takeRecentFailure(now = Date.now()): string | null {
  const id = lastFailedCallId(now);
  lastFailure = null;
  return id;
}
